import type {
  Commerce,
  ProductCategorie,
  ProductType,
  TrackedProductComponent,
} from '@analytics/models';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import { componentViewModelSelector } from '@core/experience-editor-utils';
import type { StoreProductData } from '@legacy/common-ui-models';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, type Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  selectStoreFunnelSettings,
  selectStoreReceiptViewModel,
  StoreProductTrackingConfig,
} from '../../store';
import { mockStoreReceiptViewModel } from '../../mocks/mock-store-receipt-vm';
import { StoreReceiptRendering } from '../../models/store-receipt.rendering.interface';
import { getProductSuffixLabel } from '../../utils/get-product-suffix-label';
import { initializeStoreFunnelComponent } from '../../utils/initialize-store-funnel-component';

@Component({
  selector: 'wl-store-receipt[rendering]',
  templateUrl: './store-receipt.component.html',
})
export class StoreReceiptComponent
  implements
    OnInit,
    OnDestroy,
    TrackedProductComponent<StoreProductData, Commerce>
{
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering!: StoreReceiptRendering;

  viewModel$ = this.store$.select(
    componentViewModelSelector(
      selectStoreReceiptViewModel,
      mockStoreReceiptViewModel
    )
  );

  funnelSettings$ = this.store$.select(selectStoreFunnelSettings);

  onDestroy$ = new Subject<void>();
  showPrices$ = new BehaviorSubject(false);

  constructor(
    private readonly store$: Store,
    private readonly trackingConfig: StoreProductTrackingConfig
  ) {}

  ngOnInit() {
    this.store$.dispatch(
      initializeStoreFunnelComponent(
        'orderForm',
        this.rendering.fields.FunnelSettings
      )
    );
    this.funnelSettings$
      .pipe(
        map((state) => !state.hidePrices),
        takeUntil(this.onDestroy$)
      )
      .subscribe(this.showPrices$);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  get trackProductConfig() {
    return this.trackingConfig.getConfig(
      this.productType,
      this.productCategory
    );
  }

  get productType(): ProductType | undefined {
    return this.rendering.fields.FunnelSettings?.fields?.ProductType
      ?.value as ProductType;
  }

  get productCategory(): ProductCategorie | undefined {
    return this.rendering.fields.FunnelSettings?.fields?.ProductCategory
      ?.value as ProductCategorie;
  }

  get allowMultipleProductsSelection(): boolean {
    return (
      this.rendering?.fields.FunnelSettings?.fields
        ?.AllowMultipleProductsSelection?.value ?? false
    );
  }

  /**
   * Will count up all the product prices and return the totalPrice (Observable<number>).
   */
  get totalPrice$(): Observable<number> {
    return this.viewModel$.pipe(
      map(({ selectedProducts }) =>
        selectedProducts.reduce((total, { price }) => total + (price ?? 0), 0)
      )
    );
  }

  hasAnyContent(product: StoreProductData): boolean {
    return (
      !!product.content.text ||
      !!product.content.productInfo?.length ||
      !!product.content.usps?.length
    );
  }

  get hasConclusionContent$(): Observable<boolean> {
    return this.totalPrice$.pipe(
      map((totalPrice) => {
        const hasDisclaimer =
          !!this.rendering.fields.ProductSelectionDisclaimer.value;
        const hasPrice = this.allowMultipleProductsSelection && !!totalPrice;
        return hasPrice || hasDisclaimer;
      })
    );
  }

  suffixLabel(product: StoreProductData) {
    return getProductSuffixLabel(
      product,
      this.rendering.fields.ProductPriceSuffixLabel
    );
  }

  showPrice(product: StoreProductData): Observable<boolean> {
    return this.showPrices$.pipe(
      map((showPrices) => typeof product.price === 'number' && showPrices)
    );
  }
}
